/**
 * Página WL > Configurações > Tenant interno
 *
 * REGRAS DE NEGÓCIO (one-shot, owner-only):
 *  - Apenas o owner do WL (roleSlug === 'owner_wl') pode definir.
 *  - Observador (root_admin) só pode visualizar o estado atual.
 *  - Operação é DEFINITIVA: uma única escrita, sem editar/trocar/desfazer.
 *  - A defesa em profundidade vive no banco (índice único + triggers) e na RPC
 *    `wl_set_internal_tenant` — esta tela apenas evita interações inúteis.
 */
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertTriangle, ExternalLink } from 'lucide-react';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';
import { useWlInternalTenant } from '@/hooks/wl/useWlInternalTenant';
// Lista de tenants customer carregada via RPC dentro deste arquivo (sem hook compartilhado)
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Tipo mínimo retornado por wl_list_tenants (apenas o que usamos)
interface CustomerTenantOption {
  id: string;
  name: string;
  slug: string;
}

function useCustomerTenantsForElection(enabled: boolean) {
  return useQuery({
    queryKey: ['wl-tenants-for-internal-election'],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerTenantOption[]> => {
      // wl_list_tenants já filtra automaticamente kind='customer' (v1.2.0)
      const { data, error } = await supabase.rpc('wl_list_tenants', {
        p_limit: 500,
        p_offset: 0,
      });
      if (error) throw error;
      return (data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        slug: t.slug,
      }));
    },
  });
}

export default function WlInternalTenantSettings() {
  const { roleSlug, isObserver, loading: permsLoading } = useWlPermissions();
  const { data, isLoading, setInternalTenant, isSaving } = useWlInternalTenant();

  // Guarda extra de componente: apenas owner_wl OU observador (read-only)
  if (permsLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isOwner = roleSlug === 'owner_wl';
  if (!isOwner && !isObserver) {
    return <Navigate to="/wl" replace />;
  }

  const isConfigured = data?.is_configured ?? false;

  return (
    <div className="container max-w-3xl py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Tenant interno do WL</h1>
        <p className="text-sm text-muted-foreground">
          Define qual tenant é usado pelo painel WL para gerenciar seus próprios
          módulos (CRM, suporte, financeiro, indicações).
        </p>
      </header>

      {isConfigured ? (
        <ConfiguredState data={data!} />
      ) : isOwner ? (
        <NotConfiguredOwnerState onSubmit={setInternalTenant} isSaving={isSaving} />
      ) : (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            O tenant interno ainda não foi configurado. Apenas o owner do WL pode realizar esta operação.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===========================================================================
// Estado A: ainda não configurado (owner_wl)
// ===========================================================================
function NotConfiguredOwnerState({
  onSubmit,
  isSaving,
}: {
  onSubmit: (args: any) => Promise<string>;
  isSaving: boolean;
}) {
  const [mode, setMode] = useState<'elect' | 'provision'>('elect');

  // Elect form
  const [tenantId, setTenantId] = useState<string>('');
  const tenantsQuery = useCustomerTenantsForElection(mode === 'elect');

  // Provision form
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  // Confirmação irreversível
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const isValid = useMemo(() => {
    if (!acknowledged) return false;
    if (mode === 'elect') return Boolean(tenantId);
    return name.trim().length > 0 && /^[a-z0-9-]+$/.test(slug.trim());
  }, [mode, tenantId, name, slug, acknowledged]);

  const handleConfirm = async () => {
    if (confirmText !== 'CONFIRMAR') return;
    if (mode === 'elect') {
      await onSubmit({ mode: 'elect', tenant_id: tenantId });
    } else {
      await onSubmit({ mode: 'provision', name: name.trim(), slug: slug.trim() });
    }
    setConfirmOpen(false);
    setConfirmText('');
  };

  return (
    <>
      <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/10">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <CardTitle className="text-base text-amber-900 dark:text-amber-200">
              Atenção: configuração definitiva e irreversível
            </CardTitle>
            <CardDescription className="text-amber-800 dark:text-amber-300/90">
              Uma vez salva, esta configuração não poderá ser alterada, trocada nem desfeita.
              Para mudar o tenant interno será necessária uma nova instalação do white label.
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={mode} onValueChange={(v) => setMode(v as 'elect' | 'provision')}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="elect">Eleger tenant existente</TabsTrigger>
              <TabsTrigger value="provision">Criar tenant interno</TabsTrigger>
            </TabsList>

            <TabsContent value="elect" className="pt-4 space-y-3">
              <Label>Selecione um tenant cliente</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      tenantsQuery.isLoading ? 'Carregando tenants…' : 'Escolha um tenant'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {tenantsQuery.data?.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} <span className="text-muted-foreground">({t.slug})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                O tenant escolhido será reclassificado como interno e desaparecerá das listagens
                comerciais (KPIs, lista de tenants, etc.).
              </p>
            </TabsContent>

            <TabsContent value="provision" className="pt-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="int-name">Nome do tenant interno</Label>
                <Input
                  id="int-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex.: Operação WL"
                  maxLength={120}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="int-slug">Slug (apenas a-z, 0-9 e hífen)</Label>
                <Input
                  id="int-slug"
                  value={slug}
                  onChange={(e) =>
                    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                  }
                  placeholder="ex: wl-interno"
                  maxLength={60}
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-6 flex items-start gap-2">
            <Checkbox
              id="ack"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(Boolean(v))}
            />
            <Label htmlFor="ack" className="text-sm leading-snug cursor-pointer">
              Entendo que esta operação é <strong>definitiva e irreversível</strong> e que não
              poderá ser alterada por nenhum usuário após salva.
            </Label>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              disabled={!isValid || isSaving}
              onClick={() => setConfirmOpen(true)}
              variant="default"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Definir tenant interno
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !isSaving && setConfirmOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmação final</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Esta ação é <strong>definitiva</strong>. Depois de salva, ninguém poderá alterar,
                trocar ou desfazer a vinculação do tenant interno.
              </span>
              <span className="block">
                Para confirmar, digite <strong>CONFIRMAR</strong> abaixo:
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Digite CONFIRMAR"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== 'CONFIRMAR' || isSaving}
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar e salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ===========================================================================
// Estado B: já configurado (read-only)
// ===========================================================================
function ConfiguredState({ data }: { data: NonNullable<ReturnType<typeof useWlInternalTenant>['data']> }) {
  const setAt = data.set_at ? new Date(data.set_at) : null;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <CardTitle className="text-base">Tenant interno configurado</CardTitle>
          <Badge variant="outline" className="ml-auto">Definitivo</Badge>
        </div>
        <CardDescription>
          Esta configuração é permanente. Para alterar, é necessária uma nova instalação do white label.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground">Nome</dt>
            <dd className="font-medium">{data.tenant_name}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Slug</dt>
            <dd className="font-mono text-xs">{data.tenant_slug}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Status</dt>
            <dd>
              <Badge variant="secondary">{data.tenant_status}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Configurado por</dt>
            <dd className="font-medium">{data.set_by_name ?? '—'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted-foreground">Data</dt>
            <dd>
              {setAt
                ? format(setAt, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })
                : '—'}
            </dd>
          </div>
        </dl>

        <div className="pt-2">
          <Button asChild variant="outline" size="sm">
            <a href={`/tenant?switch=${data.internal_tenant_id}`} target="_blank" rel="noreferrer">
              Abrir tenant interno
              <ExternalLink className="ml-2 h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
